import type { ValueSubtype } from "@skilitics/nautilus-core-designer-data";

/**
 * The serializable instance lives in the data layer — the tombstone stores one —
 * and is re-exported here beside the behaviour that reads it.
 */
export type { ValueSubtype } from "@skilitics/nautilus-core-designer-data";

/**
 * What a consumer will accept, built at the site beside the base `type` it
 * already passes. The comparison rides with the requirement, so per-subtype
 * semantics need no registry and no fixed vocabulary of comparison shapes: the
 * requirement is the thing that knows what it will take. Requirements never
 * enter a selector.
 */
export interface ValueSubtypeRequirement {
  id: string;
  /** Whether an offer of the same `id` is narrow enough. Absent ⇒ any offer of the id passes. */
  accepts?: (offered: ValueSubtype) => boolean;
}

/**
 * Whether an offered value may be used where a requirement is stated.
 *
 * - No requirement ⇒ pass. An unrefined slot takes any value of its base type;
 *   a colour binds into a plain string field, because colour *is* a string.
 * - Requirement present, offer absent ⇒ fail. An unconstrained string could hold
 *   anything, so it cannot satisfy a constrained slot.
 * - Both present, `id` differs ⇒ fail.
 * - Both present, `id` equal ⇒ the requirement's own comparison, default pass.
 */
export const subtypeSatisfies = (
  required: ValueSubtypeRequirement | undefined,
  offered: ValueSubtype | undefined,
): boolean => {
  if (!required) return true;
  if (!offered || offered.id !== required.id) return false;
  return required.accepts?.(offered) ?? true;
};
